/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/permissions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-permissions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/permissions-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at
 * - Required fields: id, perm_name
 * - Readonly fields: id, created_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/permissions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-permissions-api-client.ts (API client)
 * - client/api/generated_schemas/permissions.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-06-22T23:44:13.678Z
 * Source config: config/entities/permissions-entity-config.ts
 * Public fields: 4/4 total fields
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PermissionsClient,
  PermissionsQueryOptions,
} from "../generated_clients/generated-permissions-api-client.js";
import {
  PermissionsInsert,
  PermissionsUpdate,
  PermissionsFieldConfig,
} from "../../types/generated_schemas/generated-permissions-schema.js";

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/permissions_business_hooks/

// Query keys with enhanced caching strategy
export const permissionsKeys = {
  all: ["permissions"] as const,
  lists: () => [...permissionsKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...permissionsKeys.lists(), { filters }] as const,
  details: () => [...permissionsKeys.all, "detail"] as const,
  detail: (id: string) => [...permissionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) =>
    [...permissionsKeys.all, "search", query, fields] as const,
  filter: (filters: Record<string, any>) =>
    [...permissionsKeys.all, "filter", filters] as const,
  sort: (sortBy: string, ascending: boolean) =>
    [...permissionsKeys.all, "sort", sortBy, ascending] as const,
  count: (filters?: Record<string, any>) =>
    [...permissionsKeys.all, "count", filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePermissionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PermissionsClient();
};

/**
 * Hook to get all permissions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with permissions data
 */
export const usePermissionsList = (options?: PermissionsQueryOptions) => {
  const client = usePermissionsClient();

  return useQuery({
    queryKey: permissionsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single permission by ID
 *
 * @param id - The permission ID
 * @returns React Query result with single permission data
 */
export const usePermissions = (id: string) => {
  const client = usePermissionsClient();

  return useQuery({
    queryKey: permissionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/permissions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/permissions_business_hooks/
 */

/**
 * Hook to create a new permission
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, perm_name
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at
 *
 * @returns React Query mutation for creating permission
 */
export const useCreatePermissions = () => {
  const client = usePermissionsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PermissionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing permission
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at
 *
 * @returns React Query mutation for updating permission
 */
export const useUpdatePermissions = () => {
  const client = usePermissionsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PermissionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: permissionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: permissionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a permission
 *
 * @returns React Query mutation for deleting permission
 */
export const useDeletePermissions = () => {
  const client = usePermissionsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });
};

/**
 * Hook to count permissions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePermissionsCount = (
  filter?: Partial<
    Record<(typeof PermissionsFieldConfig.filterable)[number], any>
  >,
) => {
  const client = usePermissionsClient();

  return useQuery({
    queryKey: permissionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePermissionsFieldConfig = () => {
  return PermissionsFieldConfig;
};
